<script setup lang="ts">
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from '@headlessui/vue'

import {
  MinusSmallIcon,
  PlusSmallIcon
} from '@heroicons/vue/24/outline'

const productDetails = [
  {
    name: 'Features',
    items: [
      'Multiple strap configurations',
      'Spacious interior with top zip',
      'Leather handle and tabs',
      'Interior dividers',
      'Stainless strap loops',
      'Double stitched construction',
      'Water-resistant',
    ],
  },
  {
    name: 'Shipping',
    items: [
      'Multiple strap configurations',
      'Spacious interior with top zip',
      'Leather handle and tabs',
      'Interior dividers',
      'Stainless strap loops',
      'Double stitched construction',
      'Water-resistant',
    ],
  },
  {
    name: 'Care',
    items: [
      'Multiple strap configurations',
      'Spacious interior with top zip',
      'Leather handle and tabs',
      'Interior dividers',
      'Stainless strap loops',
      'Double stitched construction',
      'Water-resistant',
    ],
  },
  {
    name: 'Returns',
    items: [
      'Multiple strap configurations',
      'Spacious interior with top zip',
      'Leather handle and tabs',
      'Interior dividers',
      'Stainless strap loops',
      'Double stitched construction',
      'Water-resistant',
    ],
  },
]
</script>

<template>
  <div class="sm:p-8 md:py-16 lg:py-24 xl:max-w-screen-xl xl:mx-auto animate-pulse">
    <!-- Start Product -->
    <div class="lg:grid lg:grid-cols-2 lg:gap-x-8 lg:items-start">
      <!-- Start Product Image Gallery -->
      <TabGroup as="div" class="flex flex-col-reverse">
        <TabPanels class="w-full aspect-w-1 aspect-h-1">
          <TabPanel 
            v-for="n in 3"
            :key="n" 
          >
            <div
              class="w-full h-full bg-gray-100 border border-light-200 bg-cover bg-top rounded-tl-none rounded-b-4xl rounded-tr-4xl overflow-hidden" 
            />
          </TabPanel>
        </TabPanels>
        
        <!-- Start Product Image Selector -->
        <div class="hidden w-full max-w-2xl mx-auto mt-6 sm:block lg:max-w-none">
          <TabList class="grid grid-cols-4 gap-6">
            <Tab 
              v-for="n in 3"
              :key="n" 
              class="relative flex items-center justify-center aspect-w-1 aspect-h-1 text-sm font-medium text-gray-900 uppercase bg-white rounded-md cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring focus:ring-offset-4 focus:ring-opacity-50" 
              v-slot="{ selected }"
            >
              <span class="absolute inset-0 overflow-hidden border border-light-200 bg-cover bg-top rounded-tl-none rounded-b-3xl rounded-tr-3xl overflow-hidden">
                <img 
                  class="w-full h-full bg-blue-200" 
                />
              </span>
              <span 
                :class="[selected ? 'ring-blue-500' : 'ring-transparent', 'absolute inset-0 rounded-md ring-2 ring-offset-2 pointer-events-none']" 
                aria-hidden="true" 
              />
            </Tab>
          </TabList>
        </div>
        <!-- End Product Image Selector -->
      </TabGroup>
      <!-- End Product Image Gallery -->

      <!-- Start Product Information -->
      <div 
        class="md:px-4 mt-10 sm:px-0 sm:mt-16 lg:mt-0" 
      >
        <h1 class="text-2xl font-extrabold tracking-tight bg-blue-600 text-blue-600">
          Product Title
        </h1>

        <div class="mt-3">
          <h2 class="sr-only">
            Product Price
          </h2>
          <p class="text-xl bg-blue-400 text-blue-400">
            £0.00
          </p>
        </div>

        <div class="mt-10">
          <div 
            class="mt-4 text-sm bg-gray-700 text-gray-700" 
          >
            Free shipping on orders over £60
          </div>
        </div>

        <section aria-labelledby="details-heading" class="mt-12">
          <h2 id="details-heading" class="sr-only">
            Additional details
          </h2>

          <div class="border-t divide-y divide-blue-300">
            <Disclosure 
              as="div" 
              v-for="detail in productDetails.slice(0, 1)" 
              :key="detail.name" 
              v-slot="{ open }"
            >
              <h3>
                <DisclosureButton class="relative flex items-center justify-between w-full py-6 text-left group">
                  <span :class="[open ? 'text-blue-600' : 'text-gray-900', 'text-sm font-medium']">
                    {{ detail.name }}
                  </span>
                  <span class="flex items-center ml-6">
                    <PlusSmallIcon v-if="!open" class="block w-6 h-6 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                    <MinusSmallIcon v-else class="block w-6 h-6 text-blue-400 group-hover:text-blue-500" aria-hidden="true" />
                  </span>
                </DisclosureButton>
              </h3>
              <div v-show="open">
                <DisclosurePanel static as="div" class="pb-6 prose-sm prose prose-gray-400">
                  <ul role="list">
                    <li v-for="item in detail.items" :key="item">{{ item }}</li>
                  </ul>
                </DisclosurePanel>
              </div>
            </Disclosure>
            <Disclosure as="div" v-for="detail in productDetails.slice(1)" :key="detail.name" v-slot="{ open }">
              <h3>
                <DisclosureButton class="relative flex items-center justify-between w-full py-6 text-left group">
                  <span :class="[open ? 'text-blue-600' : 'text-gray-900', 'text-sm font-medium']">
                    {{ detail.name }}
                  </span>
                  <span class="flex items-center ml-6">
                    <PlusSmallIcon v-if="!open" class="block w-6 h-6 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                    <MinusSmallIcon v-else class="block w-6 h-6 text-blue-400 group-hover:text-blue-500" aria-hidden="true" />
                  </span>
                </DisclosureButton>
              </h3>
              <DisclosurePanel as="div" class="pb-6 prose-sm prose prose-gray-400">
                <ul role="list">
                  <li 
                    v-for="item in detail.items" 
                    :key="item">{{ item }}
                  </li>
                </ul>
              </DisclosurePanel>
            </Disclosure>
          </div>
        </section>
      </div>
      <!-- End Product Information -->
    </div>
    <!-- End Product -->
  </div>
</template>